import {AfterViewInit, Component, computed, inject, signal, Signal, viewChild} from '@angular/core';
import {Report} from '../../classes/report';
import {ReportService} from '../../services/report.service';
import {OutageService} from '../../../outage/services/outage.service';
import {GenOutageAggregationLevel} from '../../../../../../generated/serverModels/GenOutageAggregationLevel';
import {ApplicationConfig} from '../../../../classes/application-config';
import {FormControl, FormGroup} from '@angular/forms';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {UserPreference} from '../../../../classes/user-preferences';
import {State} from '../../../outage/classes/state';
import {debounceTime} from 'rxjs/operators';
import {GenOutageType} from '../../../../../../generated/serverModels/GenOutageType';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import moment from 'moment/moment';
import {MatTableDataSource} from '@angular/material/table';
import {MatDialog} from '@angular/material/dialog';
import {
    LocationFilterModalComponent,
    LocationFilterModalDataOptions,
} from '../../../../../shared/modals/location-filter-modal/location-filter-modal.component';
import {AggregationLevel, LocationData, VulcanDateTimePickerConfig, VulcanDateTimePickerForm} from '@eagle-i/vulcan-component-library';
import {filter} from 'rxjs';
import {DataService} from '../../../../services/data.service';
import {OutageBreakdown} from '../../../outage/classes/outage-breakdown';
import {FileDownload} from '../../../../classes/file-download';
import {DateTimePickerModalComponent} from '../../../../../shared/modals/date-time-picker-modal/date-time-picker-modal.component';

@Component({
    selector: 'eaglei-peripheral-outage-report',
    templateUrl: './peripheral-outage-report.component.html',
    styleUrl: './peripheral-outage-report.component.scss',
    standalone: false,
})
export class PeripheralOutageReportComponent extends Report<OutageBreakdown> implements AfterViewInit {
    private paginator = viewChild(MatPaginator);
    private sort = viewChild(MatSort);

    private reportService = inject(ReportService);
    private outageService = inject(OutageService);

    private dialog = inject(MatDialog);

    private allStates: Signal<State[]> = toSignal(inject(DataService).stateNames);

    private userPreferences: Signal<UserPreference> = toSignal(ApplicationConfig.currentUserPreferences);

    protected filterGroup = new FormGroup({
        aggregationLevel: new FormControl<GenOutageAggregationLevel>(this.userPreferences().getOutageAggregationLevel(), {
            nonNullable: true,
        }),
        date: new FormControl<Date>(ApplicationConfig.roundMinute().toDate(), {nonNullable: true}),
        locations: new FormControl<State[]>(this.userPreferences().getStates(), {nonNullable: true}),
    });

    protected outageTypes = GenOutageType;

    private aggLevel = signal(this.userPreferences().getOutageAggregationLevel());

    protected columns = computed(() => {
        let c = [];

        switch (this.aggLevel()) {
            case GenOutageAggregationLevel.state:
                c = ['state'];
                break;
            case GenOutageAggregationLevel.county:
                c = ['county', 'state'];
                break;
            case GenOutageAggregationLevel.utility:
                c = ['utility', 'county', 'state'];
                break;
            default:
                c = [];
        }

        this.outageTypes.values().forEach((outageType) => {
            c.push(outageType.name.toLowerCase());
        });

        return c;
    });

    constructor() {
        super();
        this.reportService.getReportData().subscribe((res) => this.initializeReportInfo(res));

        this.dataSource = new MatTableDataSource<OutageBreakdown>([]);

        this.filterGroup.valueChanges.pipe(debounceTime(1000), takeUntilDestroyed()).subscribe((value) => {
            this.aggLevel.set(value.aggregationLevel);
            this.getReportData();
        });

        this.filterGroup.updateValueAndValidity();
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator();
        this.dataSource.sortingDataAccessor = this.sortAccessor.bind(this);
        this.dataSource.sort = this.sort();
    }

    private getReportData(): void {
        this.outageService
            .getOutageBreakdowns(
                this.filterGroup.controls.aggregationLevel.value,
                ApplicationConfig.roundMinute(moment(this.filterGroup.controls.date.value)),
                this.filterGroup.controls.locations.value
            )
            .subscribe((res: OutageBreakdown[]) => {
                this.dataSource.data = res;
            });
    }

    protected openFilterDialog(): void {
        const config: LocationFilterModalDataOptions = {
            autoSelectCodes: this.filterGroup.controls.locations.value.map((l) => l.geoid),
            autoSelectAggregationLevel: this.filterGroup.controls.aggregationLevel.value.toString() as AggregationLevel,
            showAggregationLevels: true,
            showCounties: false,
            aggregationLevels: ['state', 'county'],
        };
        this.dialog
            .open(LocationFilterModalComponent, {
                data: config,
            })
            .afterClosed()
            .pipe(filter((data: any) => !!data))
            .subscribe((data: LocationData) => {
                if (data.aggregationLevel === 'county') {
                    data.codes = data.codes.map((c) => c.slice(0, 2));
                }

                this.filterGroup.patchValue({
                    aggregationLevel: GenOutageAggregationLevel.forName(data.aggregationLevel),
                    locations: this.allStates().filter((s) => data.codes.includes(s.geoid)),
                });
            });
    }

    private sortAccessor(row: OutageBreakdown, column: string): string | number {
        const breakdownColumn = GenOutageType.values().find((outageType) => outageType.name.toLowerCase() === column);

        if (row.breakdownMap.has(breakdownColumn)) {
            return row.breakdownMap.get(breakdownColumn);
        }

        switch (column) {
            case 'state':
                return row.stateName.toLowerCase();
            case 'county':
                return row.countyName.toLowerCase();
            case 'utility':
                return row.utilityName.toLowerCase();
            default:
                console.warn('Unknown column for sorting', column);
                return -1;
        }
    }

    protected exportTable(): void {
        let data =
            this.columns()
                .map((c) => c.replace(/ /g, '_'))
                .join() + '\n';
        this.dataSource._orderData(this.dataSource.filteredData).forEach((row) => {
            const csvRow = [FileDownload.formatCsvCell(row.stateName)];
            if (this.aggLevel() === GenOutageAggregationLevel.county) {
                csvRow.unshift(FileDownload.formatCsvCell(row.countyName));
            } else if (this.aggLevel() === GenOutageAggregationLevel.utility) {
                csvRow.unshift(FileDownload.formatCsvCell(row.utilityName));
                csvRow.unshift(FileDownload.formatCsvCell(row.countyName));
            }

            this.outageTypes.values().forEach((outageType) => {
                csvRow.push(FileDownload.formatCsvCell(row.breakdownMap.get(outageType)));
            });

            data += csvRow.join(',') + '\n';
        });

        FileDownload.downloadCSV('peripheral_outages', data, this.attributionUrl);
    }

    public openDateRangeDialog() {
        const tmpForm = new FormGroup({
            start: new FormControl(this.filterGroup.controls.date.value, {}),
        });

        const config: {
            config: VulcanDateTimePickerConfig;
            form: FormGroup;
            title: string;
        } = {
            title: 'Set Run Start Time',
            config: {
                includeTime: true,
                dateType: 'start',
                // We're assuming that our max end time and date should be the current date
                startMaxDate: ApplicationConfig.roundMinute().toDate(),
                // startMinDate: ApplicationConfig.roundMinute().subtract(30, 'days').startOf('day').toDate(),
                startMinDate: ApplicationConfig.roundMinute().startOf('year').toDate(),
            },
            form: tmpForm,
        };

        this.dialog
            .open(DateTimePickerModalComponent, {
                data: config,
                maxWidth: '720px',
            })
            .afterClosed()
            .pipe(filter((data: boolean) => !!data))
            .subscribe(() => {
                this.filterGroup.patchValue({
                    date: tmpForm.value.start,
                });
            });
    }
}
